import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Trophy, Medal, Flame, Heart, Star, Sparkles,
  TrendingUp, MessageSquare, Loader2, Crown
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";

function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <div className="w-8 h-8 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
        <Crown className="w-4 h-4 text-[#FFD700]" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-8 h-8 rounded-full bg-[#C0C0C0]/20 flex items-center justify-center">
        <Medal className="w-4 h-4 text-[#C0C0C0]" />
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-8 h-8 rounded-full bg-[#CD7F32]/20 flex items-center justify-center">
        <Medal className="w-4 h-4 text-[#CD7F32]" />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
      <span className="text-xs font-bold text-slate-400">{rank}</span>
    </div>
  );
}

function LeaderboardCard({ user, rank }) {
  const isTop3 = rank <= 3;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.05 }}
      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
        isTop3
          ? "bg-white/[0.05] backdrop-blur-xl border border-[#00FF88]/20 hover:border-[#00FF88]/40"
          : "bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] hover:border-white/10"
      }`}
      data-testid={`leaderboard-rank-${rank}`}
    >
      <RankBadge rank={rank} />

      <div className="flex items-center gap-3 flex-1 min-w-0">
        {user.profile_image_url ? (
          <img 
            src={user.profile_image_url} 
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
            <span className="text-xs font-medium text-slate-400">{user.name?.charAt(0)?.toUpperCase() || "?"}</span>
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate" style={{ fontFamily: "Manrope" }}>
            {user.name}
          </p>
          <div className="flex items-center gap-3 mt-1">
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <MessageSquare className="w-3 h-3" /> {user.total_responses} responses
            </span>
            {user.streak > 0 && (
              <span className="flex items-center gap-1 text-xs text-orange-400">
                <Flame className="w-3 h-3" /> {user.streak} streak
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 text-xs text-[#00FF88]">
        <Heart className="w-3.5 h-3.5" />
        <span className="font-bold">{user.total_reactions}</span>
      </div>
    </motion.div>
  );
}

function PopularPromptCard({ prompt, rank }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: rank * 0.05 }}
      className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl p-4 hover:border-white/10 transition-all"
      data-testid={`popular-prompt-${rank}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-6 h-6 rounded-lg bg-[#00FF88]/10 flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-[#00FF88]">{rank}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-300 leading-relaxed line-clamp-2">{prompt.prompt_text}</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-xs text-slate-500">
              {prompt.response_count} {prompt.response_count === 1 ? "response" : "responses"}
            </span>
            <span className="text-xs text-[#00FF88]">
              {prompt.shared_count} shared
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [popularPrompts, setPopularPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [lbRes, promptsRes] = await Promise.all([
        api.get("/community/leaderboard"),
        api.get("/community/prompts/popular"),
      ]);
      setLeaderboard(lbRes.data.leaderboard || []);
      setPopularPrompts(promptsRes.data.prompts || []);
    } catch {
      toast.error("Failed to load leaderboard");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[900px] mx-auto pb-24" data-testid="leaderboard-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-[#00FF88]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] font-medium">Rankings</p>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
          Leaderboard
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Top contributors and most popular prompts
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-6 bg-white/[0.02] border border-white/[0.06] rounded-xl p-1.5 w-fit">
        <button
          onClick={() => setActiveTab("users")}
          data-testid="tab-users"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === "users"
              ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
              : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
        >
          <Trophy className="w-3.5 h-3.5" /> Top Users
        </button>
        <button
          onClick={() => setActiveTab("prompts")}
          data-testid="tab-prompts"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
            activeTab === "prompts"
              ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
              : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" /> Popular Prompts
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin" />
          <p className="text-sm text-slate-400">Loading rankings...</p>
        </div>
      ) : activeTab === "users" ? (
        leaderboard.length === 0 ? (
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
            <Trophy className="w-10 h-10 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
              No rankings yet
            </h3>
            <p className="text-sm text-slate-400 max-w-sm mx-auto">
              Complete daily prompts to appear on the leaderboard!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((user, idx) => (
              <LeaderboardCard key={user.user_id} user={user} rank={idx + 1} />
            ))}
          </div>
        )
      ) : popularPrompts.length === 0 ? (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
          <Sparkles className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
            No popular prompts yet
          </h3>
          <p className="text-sm text-slate-400 max-w-sm mx-auto">
            Be the first to respond to daily prompts!
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {popularPrompts.map((prompt, idx) => (
            <PopularPromptCard key={prompt.prompt_id || idx} prompt={prompt} rank={idx + 1} />
          ))}
        </div>
      )}

      {/* How It Works */}
      <div className="mt-10 pt-6 border-t border-white/[0.06]">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
          How Rankings Work
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl p-4">
            <MessageSquare className="w-5 h-5 text-[#00FF88] mb-2" />
            <p className="text-xs text-slate-400">
              <span className="text-white font-medium">Total Responses</span> — Answer daily prompts to climb the ranks
            </p>
          </div>
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl p-4">
            <Flame className="w-5 h-5 text-orange-400 mb-2" />
            <p className="text-xs text-slate-400">
              <span className="text-white font-medium">Streaks</span> — Respond consistently to build your streak
            </p>
          </div>
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl p-4">
            <Heart className="w-5 h-5 text-red-400 mb-2" />
            <p className="text-xs text-slate-400">
              <span className="text-white font-medium">Reactions</span> — Share responses to earn community love
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
